import React, { useState } from 'react';
import { Heart, ShoppingCart, ChevronLeft, ChevronRight, Package, Truck, Shield, Award, ZoomIn, X } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { formatPrice, calculateDiscount } from '../../utils/helpers';

const ProductDetail = ({ product }) => {
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist } = useApp();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showZoom, setShowZoom] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });

  const discount = calculateDiscount(product.original_price, product.price);
  const inWishlist = isInWishlist(product.id);

  const handleAddToCart = async () => { setAddingToCart(true); await addToCart(product.id, quantity); setTimeout(() => setAddingToCart(false), 500); };
  const handleToggleWishlist = async () => { inWishlist ? await removeFromWishlist(product.id) : await addToWishlist(product.id); };
  const nextImage = () => setCurrentImageIndex(prev => (prev + 1) % product.images.length);
  const prevImage = () => setCurrentImageIndex(prev => (prev - 1 + product.images.length) % product.images.length);
  const handleImageMouseMove = (e) => { const r = e.target.getBoundingClientRect(); setZoomPosition({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }); };

  const features = [
    { icon: Package, text: 'Handcrafted by master artisans' },
    { icon: Truck, text: 'Free shipping on orders above ₹15,000' },
    { icon: Shield, text: '100% authentic traditional craft' },
    { icon: Award, text: 'Unique collectible masterpiece' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Images */}
        <div>
          <div className="relative aspect-square bg-[#F5EFE6] overflow-hidden mb-4 cursor-zoom-in group" onClick={() => setShowZoom(true)} onMouseMove={handleImageMouseMove}>
            <img src={product.images[currentImageIndex] || 'https://via.placeholder.com/600'} alt={product.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3 shadow-lg"><ZoomIn className="w-6 h-6 text-[#3E2723]" /></div>
            </div>
            {discount > 0 && <div className="absolute top-4 left-4 bg-[#FEFCF9] text-[#3E2723] px-4 py-2 text-xs font-semibold tracking-wider uppercase">{discount}% OFF</div>}
            {product.images.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 shadow-lg hover:bg-white"><ChevronLeft className="w-6 h-6 text-[#3E2723]" /></button>
                <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 shadow-lg hover:bg-white"><ChevronRight className="w-6 h-6 text-[#3E2723]" /></button>
              </>
            )}
          </div>

          {showZoom && (
            <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setShowZoom(false)}>
              <button className="absolute top-4 right-4 text-white hover:text-gray-300" onClick={() => setShowZoom(false)}><X className="w-8 h-8" /></button>
              <div className="relative max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                <img src={product.images[currentImageIndex]} alt={product.name} className="w-full h-full object-contain" style={{ transform: 'scale(2)', transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%` }} onMouseMove={handleImageMouseMove} />
              </div>
              {product.images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                  {product.images.map((img, i) => (
                    <button key={i} onClick={e => { e.stopPropagation(); setCurrentImageIndex(i); }} className={`w-16 h-16 overflow-hidden border-2 transition-all ${currentImageIndex === i ? 'border-[#C4A265]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {product.images.length > 1 && (
            <div className="flex space-x-3 overflow-x-auto">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setCurrentImageIndex(i)} className={`flex-shrink-0 w-20 h-20 overflow-hidden border-2 transition-all ${currentImageIndex === i ? 'border-[#C4A265] ring-2 ring-[#C4A265]/30' : 'border-transparent hover:border-[#E8DCC8]'}`}>
                  <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center space-x-3 mb-3">
            <span className="text-xs text-[#C4A265] bg-[#F5EFE6] px-3 py-1 font-medium tracking-[0.2em] uppercase border border-[#E8DCC8]">{product.craft_type}</span>
            <span className="text-sm text-[#7A6B5D]">{product.category}</span>
          </div>
          <h1 className="text-3xl lg:text-4xl font-serif text-[#3E2723] mb-4">{product.name}</h1>
          <div className="flex items-baseline space-x-3 mb-6">
            <span className="text-4xl font-bold text-[#3E2723]">{formatPrice(product.price)}</span>
            {product.original_price && product.original_price > product.price && <span className="text-xl text-[#A09486] line-through">{formatPrice(product.original_price)}</span>}
          </div>
          <p className="text-[#7A6B5D] leading-relaxed mb-6">{product.description}</p>
          <div className="mb-6">
            {product.stock > 5 ? <p className="text-[#7A6B5D] font-medium">In Stock ({product.stock} available)</p>
            : product.stock > 0 ? <p className="text-[#C4A265] font-medium">Only {product.stock} left - Order soon!</p>
            : <p className="text-red-500 font-medium">Out of Stock</p>}
          </div>

          {product.stock > 0 && (
            <div className="flex items-center space-x-4 mb-6">
              <label className="text-[#3E2723] font-medium text-sm tracking-wider uppercase">Quantity:</label>
              <div className="flex items-center border border-[#E8DCC8]">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-4 py-2 hover:bg-[#F5EFE6] transition-colors" data-testid="decrease-quantity">-</button>
                <span className="px-6 py-2 font-medium text-[#3E2723]" data-testid="quantity-value">{quantity}</span>
                <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="px-4 py-2 hover:bg-[#F5EFE6] transition-colors" data-testid="increase-quantity">+</button>
              </div>
            </div>
          )}

          <div className="flex space-x-4 mb-8">
            <button onClick={handleAddToCart} disabled={product.stock === 0 || addingToCart} className="flex-1 bg-[#3E2723] text-[#FEFCF9] py-4 px-6 font-semibold hover:bg-[#C4A265] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 tracking-wider uppercase text-sm" data-testid="add-to-cart-button">
              <ShoppingCart className="w-5 h-5" /><span>{addingToCart ? 'Adding...' : 'Add to Cart'}</span>
            </button>
            <button onClick={handleToggleWishlist} className="p-4 border border-[#E8DCC8] hover:bg-[#F5EFE6] transition-colors" data-testid="wishlist-button">
              <Heart className={`w-6 h-6 ${inWishlist ? 'fill-red-500 text-red-500' : 'text-[#7A6B5D]'}`} />
            </button>
          </div>

          <div className="border-t border-[#E8DCC8] pt-8 space-y-4">
            {features.map((f, i) => { const Icon = f.icon; return (
              <div key={i} className="flex items-center space-x-3"><Icon className="w-5 h-5 text-[#C4A265]" /><span className="text-[#7A6B5D]">{f.text}</span></div>
            ); })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
